<template>
  <slice
    :id="block._key"
    class="slice--products slice--products--auto slice--products--grid"
    @in="$emit('in')"
    @out="$emit('out')"
  >
    <div
      v-if="loading"
      class="grid-of-products"
      :style="{
        '--number-of-lines': block.gallery_mobile.length,
        '--number-of-lines-desktop': block.gallery.length,
      }"
    >
      <div
        v-for="i in block.gallery_mobile.length"
        :key="`mobile-${i}`"
        class="skeleton"
        :style="{
          '--product-grid-column-mobile': '1/13',
          '--product-grid-row-mobile': `${i}/${i + 1}`,
        }"
      ></div>

      <div
        v-for="i in block.gallery.length"
        :key="`desktop-${i}`"
        class="skeleton"
        :style="{
          '--product-grid-column-desktop': '1/13',
          '--product-grid-row-desktop': `${i}/${i + 1}`,
        }"
      ></div>
    </div>
    <div
      v-else
      class="grid-of-products"
      :class="`grid-of-products--${block._key}`"
    >
      <component
        :is="getCardComponent(item)"
        v-for="(item, index) of gridOfItems"
        :key="item.productDocument._id"
        :is-first="firstBlock && index === 0"
        v-bind="cardAttributes(item, index)"
      />

      <div v-html="gridStyles" />
    </div>
  </slice>
</template>

<script>
import { isAfter, isFuture } from 'date-fns'
import ListGallery from './mixins/ListGallery'
import services from '@/services'

import Slice from '@/components/Slices/Slice'

import ProductCardV2 from '@/components/Product/Card.v2'
import StockCard from '@/components/stock/StockCard'

import { extractMetafieldsOfCollection } from '@/utils/extractMetafields'
import { instianciateProduct } from '@/entities/sanity/product'

export default {
  name: 'ProductsListAutoBlock',
  components: {
    Slice,
  },
  mixins: [ListGallery],
  props: {
    block: {
      type: Object,
      required: true,
    },
    firstBlock: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: true,
    products: [],
    stockCollection: null,
    stockDates: null,
  }),
  async fetch() {
    this.loading = true

    await this.fetchProducts()

    if (this.block.type === 'soon-saleable') {
      await this.loadStockCollection()
    }

    this.updateGrid()

    this.loading = false
  },
  computed: {
    hasStockDates() {
      return this.block.type === 'soon-saleable' && this.stockDates !== null
    },
    showStockCard() {
      if (this.hasStockDates) {
        if (this.stockDates.start_date_web) {
          if (this.stockDates.start_date_web) {
            return isFuture(this.stockDates.start_date_web)
          }
        }
      }
      return false
    },
    gridStyles() {
      return `
      <style>
        @media screen and (max-width: 769px) {
          .grid-of-products--${this.block._key} {
            --number-of-lines: ${this.numberOfLines.mobile};
          }
        }

        @media screen and (min-width: 770px) {
          .grid-of-products--${this.block._key} {
            --number-of-lines-desktop: ${this.numberOfLines.desktop};
          }
        }
      </style>`
    },
  },
  methods: {
    async fetchProducts() {
      try {
        // Query to get products
        switch (this.block.type) {
          case 'soon-saleable':
            // Query to get soon saleable products
            this.products = await services.sanityService
              .getProductsSoonSaleable({
                context: this.$route.params.context,
                lang: this.$i18n.locale.slice(0, 2),
              })
              .then((res) => {
                return res.map((p) => instianciateProduct(p))
              })
              .then((products) => {
                return products.sort((a, b) => {
                  return a.dates.start - b.dates.start
                })
              })
            break
        }
      } catch (e) {
        this.$logError.captureException(e)
      }
    },
    async loadStockCollection() {
      if (
        process.env.SHOPIFY_STOCK_COLLECTION_UID &&
        process.env.SHOPIFY_STOCK_COLLECTION_UID[this.$i18n.locale] &&
        process.env.SHOPIFY_STOCK_COLLECTION_UID[this.$i18n.locale][
          this.$route.params.context
        ]
      ) {
        try {
          const collection = await services.collectionService.get(
            process.env.SHOPIFY_STOCK_COLLECTION_UID[this.$i18n.locale][
              this.$route.params.context
            ],
            { onlyDatesInMetafield: true },
            this.$i18n.locale
          )

          this.stockCollection = collection
          this.stockDates = extractMetafieldsOfCollection(collection)
        } catch (err) {
          this.$logError.captureException(err)
        }
      }
    },

    updateGrid() {
      const pp = this.products.map((p) => {
        return {
          type: 'product',
          productDocument: p,
        }
      })

      if (this.showStockCard) {
        const stockCard = {
          type: 'stock',
          productDocument: { _id: 'stock' },
          conf: {
            ...this.stockCollection,
            dates: {
              start: new Date(this.stockDates.start_date_web),
              end: new Date(this.stockDates.end_date),
              teasing: new Date(this.stockDates.teasing_date),
            },
          },
        }

        let index = pp.findIndex((p) =>
          isAfter(p.productDocument.dates.start, stockCard.conf.dates.start)
        )

        index = index === -1 ? pp.length : index

        pp.splice(index, 0, stockCard)
      }

      this.gridOfItems = pp
    },

    getCardComponent(item) {
      switch (item.type) {
        case 'stock':
          return StockCard
        default:
          return ProductCardV2
      }
    },

    cardAttributes(item, i) {
      const attr = {}

      if (item.type === 'stock') {
        attr.conf = item.conf
      } else {
        attr.productDocument = item.productDocument
      }

      return {
        ...attr,
        typeMobile: this.gridItemPositions[i]
          ? this.gridItemPositions[i].mobile.type
          : '',
        typeDesktop: this.gridItemPositions[i]
          ? this.gridItemPositions[i].desktop.type
          : '',
        style: this.gridItemPositions[i]
          ? `
          --product-grid-column-mobile: ${this.gridItemPositions[i].mobile.column};
          --product-grid-column-desktop: ${this.gridItemPositions[i].desktop.column};
          --product-grid-row-mobile: ${this.gridItemPositions[i].mobile.row};
          --product-grid-row-desktop: ${this.gridItemPositions[i].desktop.row};
          `
          : '',
        small: ['1/7', '7/13'].includes(
          this.gridItemPositions[i].mobile.column
        ),
      }
    },
  },
}
</script>
